'use client';

import 'dayjs/locale/ko';

import { Avatar } from '@common/components';
import {
  SvgBookmarkFill,
  SvgChatbubbleTypingFill,
  SvgChevronright,
  SvgSend,
} from '@common/icon';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useEmblaCarousel from 'embla-carousel-react';
import {
  type ComponentType,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { UnauthSdk } from '@/api';
import { BOOKMARK_TYPE } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import {
  DONATION_ROUTES,
  ENV,
  ROUTES,
  SELECTOR,
  WEL_MARKET_ROUTES,
} from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useGlobalStore } from '@/stores/GlobalStore';
import { buildDetailSlug } from '@/utils/detailSlug';

type FeedResponse = NonNullable<
  Awaited<ReturnType<typeof UnauthSdk.findFeeds>>['data']
>;
type FeedItem = NonNullable<
  NonNullable<FeedResponse['context']>['contents']
>[number];

dayjs.extend(relativeTime);

const formatDate = (value?: string) => {
  if (!value || !dayjs(value).isValid()) return '';

  const date = dayjs(value);

  return dayjs().diff(date, 'day') < 7
    ? date.locale('ko').fromNow()
    : date.format('YYYY.MM.DD');
};

const normalizeRate = (rate?: number) => {
  if (typeof rate !== 'number' || Number.isNaN(rate)) return 0;

  return Math.round(rate <= 1 ? rate * 100 : rate);
};

const formatValue = (value?: number, unit?: string) =>
  typeof value === 'number'
    ? `${value.toLocaleString('ko-KR')}${unit ?? ''}`
    : '-';

type FeedRouteEntry =
  | { key: 'contentIdx'; resolve: (idx: string, item: FeedItem) => string }
  | { key: 'contentId'; resolve: (id: number, item: FeedItem) => string };

const FEED_DETAIL_ROUTE_MAP: Partial<
  Record<NonNullable<FeedItem['feedContentType']>, FeedRouteEntry>
> = {
  POLICY: {
    key: 'contentIdx',
    resolve: (idx, item) =>
      ROUTES.POLICY_DETAIL.pathname({
        idIdx: buildDetailSlug(idx, item.title),
      }),
  },
  BEST_CONTENTS: {
    key: 'contentIdx',
    resolve: (idx, item) =>
      ROUTES.POLICY_INFO_DETAIL.pathname({
        idIdx: buildDetailSlug(idx, item.title),
      }),
  },
  POLICY_CARD: {
    key: 'contentIdx',
    resolve: (idx, item) =>
      ROUTES.POLICY_DETAIL.pathname({
        idIdx: buildDetailSlug(idx, item.title),
      }),
  },
  HOMETOWN_NEWS: {
    key: 'contentIdx',
    resolve: (idx, item) =>
      ROUTES.HOMETOWN_NEWS_DETAIL.pathname({
        idIdx: buildDetailSlug(idx, item.title),
      }),
  },
  DONATION: {
    key: 'contentId',
    resolve: (id) => DONATION_ROUTES.HOMETOWN_BUSINESS_DETAIL.pathname({ id }),
  },
  HOME_PRODUCT: {
    key: 'contentId',
    resolve: (id) => DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({ id }),
  },
};

const getFeedDetailHref = (item: FeedItem) => {
  if (item.feedContentType === 'LOCAL_MARKET') {
    const productNumber = item.productList?.[0]?.productId;

    return productNumber
      ? WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({ productNumber })
      : WEL_MARKET_ROUTES.HOME.pathname;
  }

  const entry = item.feedContentType
    ? FEED_DETAIL_ROUTE_MAP[item.feedContentType]
    : undefined;
  if (!entry) return;

  const value = item[entry.key];

  return value !== undefined
    ? (entry.resolve as (v: never, item: FeedItem) => string)(
        value as never,
        item,
      )
    : undefined;
};

const FEED_BOOKMARK_TYPE_MAP: Partial<
  Record<NonNullable<FeedItem['feedContentType']>, BOOKMARK_TYPE>
> = {
  POLICY: BOOKMARK_TYPE.POLICY,
  BEST_CONTENTS: BOOKMARK_TYPE.POLICY_INFO,
  POLICY_CARD: BOOKMARK_TYPE.POLICY,
  HOMETOWN_NEWS: BOOKMARK_TYPE.HOMETOWN_NEWS,
};

function FeedCardHeader({ item }: { item: FeedItem }) {
  return (
    <div className="flex items-center gap-12">
      <Avatar size={40} src={item.publisherLogoUrl} alt={item.publisherName} />
      {item.publisherName ? (
        <span className="flex-1 truncate font-600 text-Gray-900 typo-caption1">
          {item.publisherName}
        </span>
      ) : null}
      <span className="font-400 text-Gray-550 typo-caption1">
        {formatDate(item.publishAt)}
      </span>
    </div>
  );
}

function calcTruncation(el: HTMLParagraphElement, text: string) {
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
  const maxHeight = lineHeight * 2 + 1;

  const clone = el.cloneNode() as HTMLParagraphElement;
  Object.assign(clone.style, {
    position: 'fixed',
    visibility: 'hidden',
    top: '-9999px',
    width: `${el.clientWidth}px`,
  });
  document.body.appendChild(clone);

  clone.textContent = text;
  if (clone.scrollHeight <= maxHeight) {
    clone.remove();

    return null;
  }

  const suffix = '... 더보기';
  let lo = 0;
  let hi = text.length;

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    clone.textContent = text.slice(0, mid).trimEnd() + suffix;
    if (clone.scrollHeight <= maxHeight) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  clone.remove();

  return text.slice(0, lo).trimEnd();
}

function FeedCardBody({ item }: { item: FeedItem }) {
  const title = item.title?.trim() || undefined;
  const contentPreview = item.contentPreview?.trim() || undefined;
  const textRef = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !contentPreview) {
      setTruncated(null);

      return;
    }
    setTruncated(calcTruncation(el, contentPreview));
  }, [contentPreview]);

  useEffect(() => {
    const el = textRef.current;
    if (!el || !contentPreview) return;

    let prevWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === prevWidth) return;
      prevWidth = el.clientWidth;
      setTruncated(calcTruncation(el, contentPreview));
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, [contentPreview]);

  if (!title && !contentPreview) return null;

  return (
    <div className="flex flex-col gap-4">
      {title && (
        <h3 className="font-600 text-Gray-black typo-body2">{title}</h3>
      )}
      {contentPreview && (
        <p ref={textRef} className="font-400 text-Gray-800 typo-body4">
          {truncated ?? contentPreview}
          {truncated !== null ? '... ' : ' '}
          <span className="text-Gray-500 transition-colors duration-150 group-hover:text-Gray-700">
            더보기
          </span>
        </p>
      )}
    </div>
  );
}

function ImageContent({ item, priority, href }: ContentPluginProps) {
  const images = (item.attachmentList ?? []).flatMap((a) =>
    a.imageUrl ? [a.imageUrl] : [],
  );

  if (!images.length) return null;

  if (images.length === 1) {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-xsmall bg-Gray-200">
        <Image
          src={images[0]}
          alt="피드 이미지 1"
          fill
          sizes="(max-width: 600px) 100vw, 600px"
          priority={priority}
          className="object-cover"
        />
      </div>
    );
  }

  return <ImageCarousel href={href} images={images} priority={priority} />;
}

function ImageCarousel({
  images,
  priority,
  href,
}: {
  images: string[];
  priority?: boolean;
  href?: string;
}) {
  const router = useCustomRouter();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  return (
    <div className="flex flex-col gap-8">
      <div
        ref={emblaRef}
        className="cursor-grab overflow-hidden active:cursor-grabbing"
      >
        <div className="flex">
          {images.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className={cn(
                'relative aspect-square w-[calc((100%-8px)/1.5)] shrink-0 overflow-hidden rounded-xsmall bg-Gray-200',
                i < images.length - 1 && 'mr-8',
                href && 'cursor-pointer',
              )}
              onClick={href ? () => router.push(href) : undefined}
            >
              <Image
                src={url}
                alt={`피드 이미지 ${i + 1}`}
                fill
                sizes="(max-width: 600px) 67vw, 400px"
                priority={priority && i === 0}
                className="object-cover"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-4">
        {images.map((_, i) => (
          <div
            key={i}
            className={cn(
              'size-6 rounded-pill transition-colors duration-200',
              {
                'bg-Gray-900': i === activeIndex,
                'bg-Gray-300': i !== activeIndex,
              },
            )}
          />
        ))}
      </div>
    </div>
  );
}

function ProgressContent({
  item,
  priority,
}: {
  item: FeedItem;
  priority?: boolean;
}) {
  const { progress } = item;
  if (!progress) return null;

  const rate = normalizeRate(progress.rate);

  return (
    <div className="flex flex-col gap-8">
      {progress.imageUrl && (
        <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xsmall bg-Gray-200">
          <Image
            src={progress.imageUrl}
            alt={item.title ?? ''}
            fill
            sizes="(max-width: 600px) 100vw, 600px"
            priority={priority}
            className="object-cover"
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between font-600 text-Gray-600 typo-caption1">
          <span>
            {progress.label ?? '진행률'} {rate}%
          </span>
          <span>
            {formatValue(progress.currentValue, progress.valueUnit)} /{' '}
            {formatValue(progress.targetValue, progress.valueUnit)}
          </span>
        </div>
        <div className="flex items-center gap-16">
          <div className="h-4 flex-1 overflow-hidden rounded-pill bg-Gray-50">
            <div
              className="h-full rounded-pill bg-O-500 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.min(rate, 100)}%` }}
            />
          </div>
          <span className="font-600 text-O-500 typo-body4">{rate}%</span>
        </div>
      </div>
    </div>
  );
}

function ProductContent({
  item,
  priority,
}: {
  item: FeedItem;
  priority?: boolean;
}) {
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });

  if (!item.productList?.length) return null;

  return (
    <div
      ref={emblaRef}
      className="-mx-16 cursor-grab overflow-hidden active:cursor-grabbing xl:-mx-24"
    >
      <div className="flex gap-8 px-16 xl:px-24">
        {item.productList.map((product, i) => {
          const card = (
            <>
              <div className="relative size-140 overflow-hidden rounded-xsmall bg-Gray-200">
                <Image
                  src={product.productImageUrl}
                  alt={product.productName ?? ''}
                  fill
                  sizes="140px"
                  priority={priority && i === 0}
                  className="object-cover"
                />
              </div>
              {product.region && (
                <div className="mt-8 flex items-center gap-4">
                  <span className="font-600 text-Gray-600 typo-caption1">
                    {product.region}
                  </span>
                  <SvgChevronright className="size-12 text-Gray-400" />
                </div>
              )}
              <p className="mt-4 line-clamp-2 font-500 text-Gray-900 typo-body4">
                {product.productName}
              </p>
              {product.rating || product.reviewCount ? (
                <div className="mt-8 flex items-center gap-4">
                  <span className="font-400 text-Y-550 typo-caption2">
                    {Array.from({ length: 5 }, (_, s) =>
                      s < Math.round(product.rating ?? 0) ? '★' : '☆',
                    ).join('')}
                  </span>
                  <span className="font-400 text-Gray-600 typo-caption2">
                    ({product.reviewCount?.toLocaleString('ko-KR') ?? 0})
                  </span>
                </div>
              ) : null}
            </>
          );

          const key = product.productId ?? `${product.productName}-${i}`;
          const isLocalMarket = item.feedContentType === 'LOCAL_MARKET';
          const href = product.productId
            ? isLocalMarket
              ? WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
                  productNumber: product.productId,
                })
              : DONATION_ROUTES.GIFTS_DETAIL.pathname({
                  productNumber: product.productId,
                })
            : item.contentId && !isLocalMarket
              ? DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
                  id: item.contentId,
                })
              : undefined;

          return href ? (
            <CustomLink
              key={key}
              prefetch
              href={href}
              className="group w-140 shrink-0"
            >
              {card}
            </CustomLink>
          ) : (
            <div key={key} className="w-140 shrink-0">
              {card}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PolicyGroupContent({ item }: ContentPluginProps) {
  if (!item.customPolicyList?.length) return null;

  const title = item.title?.trim();

  return (
    <div className="flex flex-col gap-20">
      <div className="flex items-center gap-10">
        {title && (
          <h2 className="flex-1 truncate font-600 text-Gray-black typo-body2">
            {title}
          </h2>
        )}
        <CustomLink
          href={ROUTES.RECOMMEND_POLICY_LIST.pathname}
          className="group flex items-center gap-4 py-8"
        >
          <span className="font-600 text-Gray-600 transition-colors duration-150 typo-caption2 group-hover:text-Gray-900">
            더보기
          </span>
          <SvgChevronright className="size-10 text-Gray-600 transition-colors duration-150 group-hover:text-Gray-900" />
        </CustomLink>
      </div>
      {item.customPolicyList.map((policy) => {
        const row = (
          <div className="group/row flex items-center gap-12 px-12">
            <Avatar
              size={48}
              src={policy.publisherLogoUrl}
              alt={policy.publisherName}
            />
            <div className="flex flex-1 flex-col items-start gap-4 overflow-hidden">
              {policy.supportBenefit && (
                <span className="max-w-full truncate rounded-xsmall bg-M-25 px-6 py-2 font-600 text-M-400 typo-caption2">
                  {policy.supportBenefit}
                </span>
              )}
              <span className="w-full truncate font-600 text-Gray-black transition-colors duration-150 typo-body4 group-hover/row:text-M-500">
                {policy.title}
              </span>
            </div>
          </div>
        );

        if (policy.contentIdx) {
          return (
            <CustomLink
              key={policy.contentIdx}
              prefetch
              href={ROUTES.POLICY_DETAIL.pathname({ idIdx: policy.contentIdx })}
            >
              {row}
            </CustomLink>
          );
        }

        return <div key={policy.contentId ?? policy.title}>{row}</div>;
      })}
    </div>
  );
}

type ContentPluginProps = {
  item: FeedItem;
  priority?: boolean;
  href?: string;
};

const CONTENT_PLUGINS: Partial<
  Record<NonNullable<FeedItem['feedType']>, ComponentType<ContentPluginProps>>
> = {
  SINGLE_IMAGE: ImageContent,
  MULTI_IMAGE: ImageContent,
  PROGRESS: ProgressContent,
  PRODUCT: ProductContent,
  POLICY_GROUP: PolicyGroupContent,
};

function FeedCardActions({ item, href }: { item: FeedItem; href?: string }) {
  const commentCount = item.commentCount ?? 0;
  const router = useCustomRouter();
  const { bookmark } = useBookmark();
  const setShareLink = useGlobalStore((state) => state.setShareLink);
  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });
  const isBookmarked = item.wishlistYN ?? false;

  const bookmarkType = item.feedContentType
    ? FEED_BOOKMARK_TYPE_MAP[item.feedContentType]
    : undefined;
  const bookmarkContentId = item.contentId;

  const handleComment = () => {
    if (!href) return;
    router.push(
      item.feedContentType === 'LOCAL_MARKET'
        ? `${href}?tab=comments`
        : `${href}#${SELECTOR.COMMENT_LIST_TOP}`,
    );
  };

  const handleShare = () => {
    if (!href) return;
    requestHaptic();
    setShareLink(`${ENV.NEXT_PUBLIC_DOMAIN}${href}`);
  };

  const handleBookmark = () => {
    if (!bookmarkType || !bookmarkContentId) return;
    bookmark({
      contentId: bookmarkContentId,
      isBookmarked,
      bookmarkType,
      contentName: item.title,
    });
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-12">
        {bookmarkType && (
          <button
            className="group flex items-center gap-2"
            aria-label="댓글"
            onClick={handleComment}
          >
            <SvgChatbubbleTypingFill className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" />
            {commentCount > 0 && (
              <span className="font-600 text-Gray-600 typo-caption1">
                {commentCount.toLocaleString('ko-KR')}
              </span>
            )}
          </button>
        )}
        {href && (
          <button className="group" aria-label="공유" onClick={handleShare}>
            <SvgSend className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" />
          </button>
        )}
      </div>
      {bookmarkType && bookmarkContentId && (
        <button
          className="group"
          aria-label={isBookmarked ? '북마크 해제' : '북마크'}
          onClick={handleBookmark}
        >
          <SvgBookmarkFill
            className={cn(
              'size-20 text-Gray-400 transition-colors duration-150',
              {
                'text-B-500': isBookmarked,
                'group-hover:text-Gray-600': !isBookmarked,
              },
            )}
          />
        </button>
      )}
    </div>
  );
}

function FeedCardComment({ item, href }: { item: FeedItem; href?: string }) {
  const comment = item.representComment;
  if (!comment?.comment) return null;

  const content = (
    <>
      <div className="flex items-center gap-8">
        <div className="flex items-center gap-4">
          <Avatar
            size={24}
            src={comment.profileImage}
            alt={comment.nickname ?? '익명'}
          />
          <span className="font-600 text-Gray-900 typo-caption2">
            {comment.nickname ?? '익명'}
          </span>
        </div>
        <span className="font-400 text-Gray-550 typo-caption2">
          {formatDate(comment.createdAt)}
        </span>
      </div>
      <p className="line-clamp-1 pl-28 font-400 text-Gray-600 typo-caption1">
        {comment.comment}
      </p>
    </>
  );

  if (href) {
    return (
      <CustomLink prefetch href={`${href}#${SELECTOR.COMMENT_LIST_TOP}`}>
        {content}
      </CustomLink>
    );
  }

  return <div>{content}</div>;
}

export const FeedCard = memo(function FeedCard({
  item,
  priority,
}: {
  item: FeedItem;
  priority?: boolean;
}) {
  const ContentPlugin = item.feedType
    ? CONTENT_PLUGINS[item.feedType]
    : undefined;

  const href = getFeedDetailHref(item);
  const isPolicyGroup = item.feedType === 'POLICY_GROUP';
  const isProduct = item.feedType === 'PRODUCT';
  // 캐러셀(다중 이미지)은 <a> 안에 두면 link-drag와 충돌해 PC 마우스 드래그가 막힌다.
  const isOutsideLink =
    isProduct || item.feedType === 'MULTI_IMAGE';

  const linkedContent = isPolicyGroup ? (
    ContentPlugin && <ContentPlugin item={item} priority={priority} />
  ) : (
    <>
      <FeedCardHeader item={item} />
      <FeedCardBody item={item} />
      {!isOutsideLink && ContentPlugin && (
        <ContentPlugin item={item} priority={priority} />
      )}
    </>
  );

  return (
    <article className="bg-Gray-white px-16 py-24 xl:px-24">
      <div className="flex flex-col gap-20">
        {href ? (
          <CustomLink
            prefetch
            href={href}
            className="group flex flex-col gap-8"
          >
            {linkedContent}
          </CustomLink>
        ) : (
          <div className="flex flex-col gap-8">{linkedContent}</div>
        )}
        {isOutsideLink && ContentPlugin && (
          <ContentPlugin href={href} item={item} priority={priority} />
        )}
        {!isProduct && !isPolicyGroup && (
          <>
            <FeedCardActions item={item} href={href} />
            <FeedCardComment item={item} href={href} />
          </>
        )}
      </div>
    </article>
  );
});
